import { ChangeDetectionStrategy, Component, ViewEncapsulation, computed, input } from '@angular/core';

export type TSignalQuality = 'Excellent' | 'Good' | 'Fair' | 'Poor' | 'Offline';

/**
 * Resolves signal quality from a telemetry payload.
 * Centralizes the logic so it doesn't need to be duplicated across consumers.
 *
 * @param payload - An object with optional ToQuality, FromQuality, ToConnectivity, and To fields.
 * @returns The resolved TSignalQuality, or null if no quality data is available.
 */
export function resolveSignalQuality(
  payload: {
    ToQuality?: string;
    FromQuality?: string;
    ToConnectivity?: string;
    To?: string;
  } | null | undefined
): TSignalQuality | null {
  if (!payload) return null;

  const toStatus = (payload?.ToConnectivity ?? payload?.To ?? '').trim().toLowerCase();
  if (toStatus === 'offline') return 'Offline';

  const to = (payload?.ToQuality ?? '').trim().toLowerCase();
  const from = (payload?.FromQuality ?? '').trim().toLowerCase();
  const current = to || from;

  if (!current) return null;

  if (current === 'poor') return 'Poor';
  if (current === 'fair') return 'Fair';
  if (current === 'good') return 'Good';
  if (current === 'excellent') return 'Excellent';

  return null;
}

/**
 * Maps a raw access status to the signal bars state used by the shared icon.
 */
export function resolveSignalQualityFromStatus(status: string | null | undefined): TSignalQuality | null {
  const normalized = (status ?? '').trim().toLowerCase();

  if (normalized === 'online') return 'Excellent';
  if (normalized === 'offline' || normalized === 'unknown') return 'Offline';

  return null;
}

@Component({
  selector: 'lib-signal-quality-icon',
  standalone: true,
  template: `
    <div
      class="signal-quality-icon"
      [class.excellent]="resolvedQuality() === 'Excellent'"
      [class.good]="resolvedQuality() === 'Good'"
      [class.fair]="resolvedQuality() === 'Fair'"
      [class.poor]="resolvedQuality() === 'Poor'"
      [class.small]="small()"
      [attr.aria-label]="resolvedQuality() ? 'Signal quality: ' + resolvedQuality() : null"
      role="img"
    >
      <span class="bar"></span>
      <span class="bar"></span>
      <span class="bar"></span>
      <span class="bar"></span>
    </div>
  `,
  styles: [
    `
      .signal-quality-icon {
        --signal-muted: var(--disabled);
        --signal-success: var(--success);
        --signal-success-light: var(--success);
        --signal-warning: var(--warning);
        --signal-danger: var(--danger);
        display: flex;
        align-items: flex-end;
        gap: 2px;
        height: 18px;
        width: 20px;
        margin-bottom: -3px;
        justify-content: center;
      }
      .signal-quality-icon .bar {
        width: 4px;
        border-radius: 2px;
        flex-shrink: 0;
        background: var(--signal-muted);
      }
      .signal-quality-icon .bar:nth-child(1) { height: 6px; }
      .signal-quality-icon .bar:nth-child(2) { height: 10px; }
      .signal-quality-icon .bar:nth-child(3) { height: 14px; }
      .signal-quality-icon .bar:nth-child(4) { height: 18px; }
      
      .signal-quality-icon.excellent .bar:nth-child(1),
      .signal-quality-icon.excellent .bar:nth-child(2),
      .signal-quality-icon.excellent .bar:nth-child(3),
      .signal-quality-icon.excellent .bar:nth-child(4) { background: var(--signal-success); }
      
      .signal-quality-icon.good .bar:nth-child(1),
      .signal-quality-icon.good .bar:nth-child(2),
      .signal-quality-icon.good .bar:nth-child(3) { background: var(--signal-success-light); }
      
      .signal-quality-icon.fair .bar:nth-child(1),
      .signal-quality-icon.fair .bar:nth-child(2) { background: var(--signal-warning); }
      
      .signal-quality-icon.poor .bar:nth-child(1) { background: var(--signal-danger); }

      .signal-quality-icon.small { height: 10px; }
      .signal-quality-icon.small .bar:nth-child(1) { height: 4px; }
      .signal-quality-icon.small .bar:nth-child(2) { height: 7px; }
      .signal-quality-icon.small .bar:nth-child(3) { height: 10px; }
      .signal-quality-icon.small .bar:nth-child(4) { height: 13px; }
    `,
  ],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SignalQualityIconComponent {
  quality = input<TSignalQuality | null>(null);
  status = input<string | null>(null);
  small = input<boolean>(false);

  readonly resolvedQuality = computed(() => resolveSignalQualityFromStatus(this.status()) ?? this.quality());
}
